import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Card, Button, Table, Modal, Form, Input, InputNumber, Switch, Space, message, Empty, Tag, TreeSelect } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, FolderAddOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { DataNode } from 'antd/es/tree';
import { getCategoryTree, createCategory, updateCategory, deleteFoodItem, FoodCategory } from '../../api/foods';

const Categories: React.FC = () => {
  const [treeData, setTreeData] = useState<FoodCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState<FoodCategory | null>(null);
  const [form] = Form.useForm();

  const collectDescendantIds = useCallback((cats: FoodCategory[], parentId: string): string[] => {
    const ids: string[] = [];
    for (const c of cats) {
      if (String(c.parentId) === parentId) {
        ids.push(String(c.id), ...collectDescendantIds(cats, String(c.id)));
      }
    }
    return ids;
  }, []);

  const flatTree = useMemo(() => {
    const excludeIds = new Set<string>();
    if (editingCategory) {
      excludeIds.add(String(editingCategory.id));
      const desc = collectDescendantIds(treeData, String(editingCategory.id));
      desc.forEach((id) => excludeIds.add(id));
    }
    const toDataNode = (cats: FoodCategory[]): DataNode[] =>
      cats
        .filter((c) => !excludeIds.has(c.id))
        .map((c) => ({
          key: c.id,
          title: c.name,
          value: c.id,
          children: c.children ? toDataNode(c.children) : undefined,
        }));
    return toDataNode(treeData);
  }, [treeData, editingCategory, collectDescendantIds]);

  const fetchTree = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCategoryTree();
      setTreeData(data ?? []);
    } catch {
      message.error('获取分类失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTree(); }, [fetchTree]);

  const handleAdd = (parentId?: string | null) => {
    setEditingCategory(null);
    form.resetFields();
    if (parentId) form.setFieldsValue({ parentId });
    setModalVisible(true);
  };

  const handleEdit = (category: FoodCategory) => {
    setEditingCategory(category);
    form.setFieldsValue(category);
    setModalVisible(true);
  };

  const handleDelete = (category: FoodCategory) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除分类 "${category.name}" 吗？其下的子分类将变为根分类。`,
      okText: '确认', okType: 'danger', cancelText: '取消',
      onOk: async () => {
        try {
          await deleteFoodItem(category.id);
          message.success('删除成功');
          fetchTree();
        } catch { /* handled */ }
      },
    });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingCategory) {
        await updateCategory(editingCategory.id, values);
        message.success('更新成功');
      } else {
        await createCategory(values);
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchTree();
    } catch { /* handled */ }
  };

  const columns: ColumnsType<FoodCategory> = [
    {
      title: '分类名称',
      dataIndex: 'name',
      key: 'name',
      width: '40%',
      render: (name: string, record) => (
        <span style={{ fontWeight: record.children?.length ? 600 : 400 }}>{name}</span>
      ),
    },
    {
      title: '排序',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 100,
      align: 'center',
      render: (val: number) => val != null ? <Tag>{val}</Tag> : '-',
    },
    {
      title: '状态',
      dataIndex: 'isEnabled',
      key: 'isEnabled',
      width: 100,
      align: 'center',
      render: (isEnabled: boolean) =>
        isEnabled ? <Tag color="green">启用</Tag> : <Tag color="red">已禁用</Tag>,
    },
    {
      title: '操作',
      key: 'actions',
      width: 160,
      fixed: 'right',
      align: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" icon={<FolderAddOutlined />} onClick={() => handleAdd(record.id)}>
            添加子分类
          </Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Card
        title="食物分类"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => handleAdd(null)}>
            添加根分类
          </Button>
        }
        styles={{ body: { flex: 1, overflow: 'auto', padding: 24 } }}
        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        <Table<FoodCategory>
          columns={columns}
          dataSource={treeData}
          rowKey="id"
          loading={loading}
          pagination={false}
          expandable={{
            defaultExpandAllRows: true,
            childrenColumnName: 'children',
          }}
          size="middle"
          scroll={{ x: 'max-content', y: 'calc(100vh - 290px)' }}
          locale={{ emptyText: <Empty description="暂无分类，点击上方按钮添加" /> }}
        />
      <Modal
        title={editingCategory ? '编辑分类' : '添加分类'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="分类名称" name="name" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="如: 主食、蔬菜" />
          </Form.Item>
          <Form.Item label="排序" name="sortOrder">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="上级分类" name="parentId">
            <TreeSelect
              allowClear
              placeholder="无（根分类）"
              treeData={flatTree}
              treeDefaultExpandAll
              treeNodeFilterProp="title"
              showSearch
            />
          </Form.Item>
          {editingCategory && (
            <Form.Item label="启用" name="isEnabled" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </Card>
    </div>
  );
};

export default Categories;
